import React,{useState} from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import ReactQuill from 'react-quill';
import DOMPurify from 'dompurify';
import SafeIcon from '../common/SafeIcon';
import 'react-quill/dist/quill.snow.css';

const {FiArrowLeft,FiDownload,FiEye,FiEdit3,FiSave,FiX}=FiIcons;

const DocumentPreview=({data,onBack})=> {
  const [isEditing,setIsEditing]=useState(false);
  const [editedContent,setEditedContent]=useState('');
  const [savedContent,setSavedContent]=useState(''); // This will store the saved edited content
  const [hasUnsavedChanges,setHasUnsavedChanges]=useState(false);

  const formatDate=(dateString)=> {
    return new Date(dateString).toLocaleDateString('en-US',{year: 'numeric',month: 'long',day: 'numeric'});
  };

  const generateFormattedContent=()=> {
    // Convert services list to bullet points if it contains newlines or dashes 
    const formatServicesList=(services)=> {
      if (!services) return '';
      const lines=services.split('\n').filter(line=> line.trim());
      return lines.map(line=> {
        const cleanLine=line.trim().replace(/^[-•]\s*/,'');
        return `<li>${cleanLine}</li>`;
      }).join('');
    };

    const formatClassroomRules=(rules)=> {
      if (!rules) return '';
      const lines=rules.split('\n').filter(line=> line.trim());
      return lines.map(line=> {
        const cleanLine=line.trim().replace(/^[-•]\s*/,'');
        return `<li>${cleanLine}</li>`;
      }).join('');
    };

    const inPersonSection=data.in_person_offered==='Yes' ? `<p>I offer <strong>in-person services</strong>. Requirements:</p> <p>${data.in_person_details}</p>` : '<p>I currently offer <strong>online-only services</strong>.</p>';

    // Generate expiration text for both one-off and subscription 
    const expirationText=` <p><strong>Credit Expiration Policies:</strong></p> <ul> <li><strong>One-off purchases:</strong> Credits expire after <strong>${data.credit_expiration_months} months</strong>,unless otherwise stated.</li> <li><strong>Subscription purchases:</strong> Credits expire after <strong>${data.subscription_expiration_days} days</strong>.</li> </ul> `;

    return ` <h1>Terms & Conditions for ${data.business_name}</h1> <div class="info-box"> <p><strong>Educator:</strong> ${data.educator_name}</p> <p><strong>Business Name:</strong> ${data.business_name}</p> <p><strong>Location:</strong> ${data.business_location}</p> <p><strong>Contact:</strong> <a href="mailto:${data.contact_email}">${data.contact_email}</a></p> <p><strong>Booking Page:</strong> <a href="${data.booking_link}" target="_blank">${data.booking_link}</a></p> <p><strong>Last updated:</strong> ${formatDate(data.last_updated_date)}</p> </div> <hr /> <h2>1. Introduction</h2> <p>These <strong>Terms & Conditions</strong> ("<em>Terms</em>") govern the services provided by ${data.business_name} ("<em>I</em>","<em>me</em>","<em>my</em>"). By booking any class,session,or service,you ("<em>you</em>","<em>your</em>","<em>the student/client</em>") agree to these Terms.</p> <h2>2. Services Provided</h2> <p>I provide the following services:</p> <ul> ${formatServicesList(data.services_list)} </ul> <p>All service details such as schedule,pricing,and requirements are listed on my booking page.</p> <h2>3. Booking & Payments</h2> <p>All bookings must be made through: <a href="${data.booking_link}" target="_blank">${data.booking_link}</a></p> <p>A booking is <strong>confirmed only when payment is completed</strong>.</p> <p>You must attend classes at the scheduled time.</p> <h2>4. Attendance & Lateness</h2> <ul> <li>If you arrive late,the class still ends at the scheduled time.</li> <li>If you miss a class without cancelling,it counts as a <strong>no-show</strong>.</li> </ul> <h2>5. Cancellation Policy</h2> <h3>Student cancellations</h3> <ul> <li>You may cancel up to <strong>${data.cancellation_window_hours} hours</strong> before the class.</li> <li>On-time cancellations qualify for a class credit (see refund policy).</li> <li>Late cancellations <strong>do not qualify</strong> for a class credit.</li> </ul> <h3>Rescheduling</h3> <ul> <li>You may reschedule up to <strong>${data.reschedule_window_hours} hours</strong> before the class.</li> <li>Rescheduling inside the late window is considered a cancellation.</li> </ul> <h3>Educator cancellations</h3> <p>If I cancel a class,you will automatically receive a class credit.</p> <h2>6. Refund Policy (Class-Credit Only)</h2> <ul> <li>Refunds are issued as <strong>class credits only</strong>,not cash.</li> <li>This follows the refund processing rules of the SuperTeacher platform.</li> <li>Credits can be used for any future class with me.</li> <li>Credits are <strong>non-transferable</strong>.</li> </ul> ${expirationText} <h2>7. Classroom Conduct</h2> <p>Students must:</p> <ul> <li>Participate respectfully</li> <li>Follow instructions</li> <li>Use appropriate language</li> <li>Comply with the camera policy: <strong>${data.camera_policy}</strong></li> <li>Comply with the headphones policy: <strong>${data.headphones_policy}</strong></li> </ul> ${data.classroom_rules ? ` <p>Additional rules:</p> <ul> ${formatClassroomRules(data.classroom_rules)} </ul> ` : ''} <h2>8. Technology Requirements</h2> <p>Students are responsible for:</p> <ul> <li>Stable internet connection</li> <li>Working device</li> <li>Required software (Zoom,SuperTeacher classroom,etc.)</li> </ul> <p><strong>Technical issues on the student's side are not grounds for refunds.</strong></p> <h2>9. Health & Safety</h2> ${inPersonSection} <h2>10. Intellectual Property</h2> <p>All class materials,slides,worksheets,and recordings belong to <strong>${data.business_name}</strong> and may not be shared,copied,or redistributed without permission.</p> <h2>11. Privacy & Data Protection</h2> <p>I collect only the data necessary to deliver my services. Data is not shared with third parties except essential service providers such as SuperTeacher.</p> <h2>12. Limitation of Liability</h2> <p>I am not liable for technical failures,misuse of materials,or injuries during in-person sessions unless caused by my negligence.</p> <p>My liability is limited to the amount paid for the specific service purchased.</p> <h2>13. Changes to Terms</h2> <p>I may update these Terms at any time. The <strong>"Last updated"</strong> date will always reflect the most recent version.</p> <h2>14. Contact</h2> <p>For questions or support:</p> <ul> <li>📧 <a href="mailto:${data.contact_email}">${data.contact_email}</a></li> <li>🌐 <a href="${data.booking_link}" target="_blank">${data.booking_link}</a></li> </ul> <hr /> <p class="footer">Built with ❤️ by <a href="https://super-teacher.net" target="_blank">SuperTeacher</a></p> `;
  };

  const getDisplayContent=()=> {
    // Show saved content if available, otherwise show current editing content or generated content
    const content=savedContent || (isEditing ? editedContent : generateFormattedContent());
    return DOMPurify.sanitize(content);
  };

  const getCurrentContent=()=> {
    // Return saved content if available, otherwise return current editing content or generated content
    return savedContent || (isEditing ? editedContent : generateFormattedContent());
  };

  const downloadAsDoc=()=> {
    // Use the current content (saved if available, otherwise edited or generated)
    const content=getCurrentContent();
    
    // Create HTML content for Word document 
    const htmlContent=` <!DOCTYPE html> <html> <head> <meta charset="utf-8"> <title>Terms & Conditions - ${data.business_name}</title> <style> body {font-family: 'Segoe UI',Arial,sans-serif;line-height: 1.6;color: #333;margin: 40px;} h1 {text-align: center;color: #2c3e50;margin-bottom: 20px;font-size: 28px;font-weight: 700;} h2 {color: #2c3e50;font-size: 22px;margin-top: 30px;margin-bottom: 15px;font-weight: 600;border-bottom: 2px solid #3498db;padding-bottom: 5px;} h3 {color: #34495e;font-size: 18px;margin-top: 20px;margin-bottom: 10px;font-weight: 600;} ul {padding-left: 25px;margin: 15px 0;} li {margin: 8px 0;line-height: 1.5;} a {color: #3498db;text-decoration: underline;} .info-box {margin: 30px 0;padding: 20px;background-color: #f8f9fa;border-left: 5px solid #3498db;border-radius: 5px;} .info-box p {margin: 5px 0;} .footer {text-align: center;font-style: italic;color: #666;margin-top: 40px;} hr {border: none;border-top: 3px solid #3498db;margin: 30px 0;} p {margin: 15px 0;line-height: 1.6;} strong {color: #2c3e50;} </style> </head> <body> ${content} </body> </html> `;
    
    const blob=new Blob([htmlContent],{type: 'application/msword'});
    const url=URL.createObjectURL(blob);
    const link=document.createElement('a');
    link.href=url;
    link.download=`${data.business_name.replace(/[^a-z0-9]/gi,'_').toLowerCase()}_terms_conditions.doc`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const downloadAsPDF=()=> {
    // Use the current content (saved if available, otherwise edited or generated)
    const content=getCurrentContent();
    const printWindow=window.open('','_blank');
    printWindow.document.write(` <!DOCTYPE html> <html> <head> <title>Terms & Conditions - ${data.business_name}</title> <style> body {font-family: 'Segoe UI',Arial,sans-serif;line-height: 1.6;color: #333;margin: 40px;} h1 {text-align: center;color: #2c3e50;margin-bottom: 20px;font-size: 28px;font-weight: 700;} h2 {color: #2c3e50;font-size: 22px;margin-top: 30px;margin-bottom: 15px;font-weight: 600;border-bottom: 2px solid #3498db;padding-bottom: 5px;page-break-before: auto;} h3 {color: #34495e;font-size: 18px;margin-top: 20px;margin-bottom: 10px;font-weight: 600;} ul {padding-left: 25px;margin: 15px 0;} li {margin: 8px 0;line-height: 1.5;} a {color: #3498db;text-decoration: underline;} .info-box {margin: 30px 0;padding: 20px;background-color: #f8f9fa;border-left: 5px solid #3498db;border-radius: 5px;} .info-box p {margin: 5px 0;} .footer {text-align: center;font-style: italic;color: #666;margin-top: 40px;} hr {border: none;border-top: 3px solid #3498db;margin: 30px 0;} p {margin: 15px 0;line-height: 1.6;} strong {color: #2c3e50;} </style> </head> <body> ${content} </body> </html> `);
    printWindow.document.close();
    printWindow.print();
  };

  const handleEdit=()=> {
    // Load the current content (saved if available, otherwise generated)
    const currentContent=savedContent || generateFormattedContent();
    setEditedContent(currentContent);
    setIsEditing(true);
    setHasUnsavedChanges(false);
  };

  const handleSaveEdit=()=> {
    // Save the edited content to savedContent state
    setSavedContent(editedContent);
    setIsEditing(false);
    setHasUnsavedChanges(false);
  };

  const handleCancelEdit=()=> {
    setIsEditing(false);
    setEditedContent('');
    setHasUnsavedChanges(false);
  };

  const handleContentChange=(content)=> {
    setEditedContent(content);
    setHasUnsavedChanges(true);
  };

  const quillModules={toolbar: [ [{'header': [1,2,3,false]}],['bold','italic','underline','strike'],[{'list': 'ordered'},{'list': 'bullet'}],[{'color': []},{'background': []}],['link'],['clean'] ],};

  const quillFormats=[ 'header','bold','italic','underline','strike','list','bullet','color','background','link' ];

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <motion.div 
        initial={{opacity: 0,y: -20}}
        animate={{opacity: 1,y: 0}} 
        className="flex items-center justify-between mb-6"
      >
        <button
          onClick={onBack}
          className="flex items-center text-gray-600 hover:text-gray-800 transition-colors"
        >
          <SafeIcon icon={FiArrowLeft} className="mr-2" />
          Back to Form
        </button>
        
        <div className="flex items-center space-x-4">
          <div className="flex items-center text-green-600">
            <SafeIcon icon={FiEye} className="mr-2" />
            <span className="font-medium">
              {isEditing ? 'Editing' : 'Preview Ready'}
              {isEditing && hasUnsavedChanges && ' (Unsaved)'}
              {!isEditing && savedContent && ' (Edited)'}
            </span>
          </div>
          {!isEditing ? (
            <button
              onClick={handleEdit}
              className="bg-gray-600 hover:bg-gray-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors duration-200 flex items-center"
            >
              <SafeIcon icon={FiEdit3} className="mr-2" />
              Edit
            </button>
          ) : (
            <div className="flex items-center space-x-2">
              <button
                onClick={handleCancelEdit}
                className="bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center"
              >
                <SafeIcon icon={FiX} className="mr-2" />
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                className={`${hasUnsavedChanges ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-500 hover:bg-gray-600'} text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center`}
              >
                <SafeIcon icon={FiSave} className="mr-2" />
                {hasUnsavedChanges ? 'Save' : 'Saved'}
              </button>
            </div>
          )}
        </div>
      </motion.div>

      {/* Document Preview/Edit */}
      <motion.div 
        initial={{opacity: 0,y: 20}}
        animate={{opacity: 1,y: 0}} 
        className="bg-white rounded-xl shadow-lg overflow-hidden"
      >
        <div className="bg-gray-50 px-6 py-4 border-b">
          <h2 className="text-xl font-semibold text-gray-800">Document Preview</h2>
          <p className="text-gray-600 text-sm mt-1">
            {isEditing ? 'Edit your document content using the rich text editor' : 'Review your Terms & Conditions before downloading'}
          </p>
        </div>
        
        <div className="p-6" style={{minHeight: '600px'}}>
          {!isEditing ? (
            <div 
              className="document-preview"
              style={{
                fontFamily: "'Segoe UI',Arial,sans-serif",
                lineHeight: '1.6',
                color: '#333'
              }}
              dangerouslySetInnerHTML={{__html: getDisplayContent()}}
            />
          ) : (
            <div className="border border-gray-300 rounded-lg overflow-hidden">
              <ReactQuill
                value={editedContent}
                onChange={handleContentChange}
                modules={quillModules}
                formats={quillFormats}
                placeholder="Edit your document content here..."
                style={{height: '400px'}}
                theme="snow"
              />
              <div className="bg-gray-50 px-4 py-2 border-t border-gray-200">
                <p className="text-xs text-gray-500">
                  Use the rich text editor above to format your document. You can add headings,bold text,lists,and more. 
                  {hasUnsavedChanges && ' You have unsaved changes - make sure to save before downloading!'}
                </p>
              </div>
            </div>
          )}
        </div>
        
        <div className="bg-gray-50 px-6 py-4 border-t">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Document generated for: <strong>{data.business_name}</strong>
              {isEditing && hasUnsavedChanges && (
                <span className="ml-2 text-orange-600 font-medium">⚠️ Unsaved changes</span>
              )}
              {!isEditing && savedContent && (
                <span className="ml-2 text-green-600 font-medium">✓ Edited</span>
              )}
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={downloadAsPDF}
                className="bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center"
                title={isEditing && hasUnsavedChanges ? "Please save your changes first" : "Download as PDF"}
                disabled={isEditing && hasUnsavedChanges}
              >
                <SafeIcon icon={FiDownload} className="mr-2 text-sm" />
                PDF
              </button>
              <button
                onClick={downloadAsDoc}
                className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center"
                title={isEditing && hasUnsavedChanges ? "Please save your changes first" : "Download as DOC"}
                disabled={isEditing && hasUnsavedChanges}
              >
                <SafeIcon icon={FiDownload} className="mr-2 text-sm" />
                DOC
              </button>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Download Instructions */}
      <motion.div 
        initial={{opacity: 0}}
        animate={{opacity: 1}} 
        transition={{delay: 0.2}}
        className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4"
      >
        <div className="flex items-start">
          <SafeIcon icon={FiDownload} className="text-blue-600 mt-1 mr-3 flex-shrink-0" />
          <div>
            <h3 className="font-medium text-blue-800 mb-1">Download Instructions</h3>
            <p className="text-blue-700 text-sm">
              <strong>DOC format:</strong> Opens in Microsoft Word,Google Docs,and other word processors for editing.<br />
              <strong>PDF format:</strong> Perfect for sharing and printing with professional formatting.<br />
              <strong>💡 Tip:</strong> If you make edits,save them before downloading to include your changes!
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default DocumentPreview;